import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  Save as SaveIcon,
  UploadFile as UploadIcon,
} from '@mui/icons-material';
import { AI_SERVICE_URL } from '../../services/workerUrls';

type ViewMode = 'intent' | 'narrativeExtension' | 'entityCreator' | 'actionExecution' | 'narrator' | 'lastInput' | 'lastOutput';

interface PromptsData {
  intent: string;
  narrativeExtension: string;
  entityCreator: string;
  actionExecution: string;
  narrator: string;
}

export function AIConsolePanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('intent');
  const [prompts, setPrompts] = useState<PromptsData>({
    intent: '',
    narrativeExtension: '',
    entityCreator: '',
    actionExecution: '',
    narrator: '',
  });
  const [originalPrompts, setOriginalPrompts] = useState<PromptsData>({
    intent: '',
    narrativeExtension: '',
    entityCreator: '',
    actionExecution: '',
    narrator: '',
  });
  const [loadSuccess, setLoadSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [lastInput, setLastInput] = useState('');
  const [lastOutput, setLastOutput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadPrompts = async () => {
    setLoading(true);
    setError(null);
    setLoadSuccess(false);

    try {
      const response = await fetch(`${AI_SERVICE_URL()}/api/admin/prompts`);

      if (!response.ok) {
        throw new Error(`Failed to load prompts: HTTP ${response.status}`);
      }

      const data: { prompts: PromptsData } = await response.json();
      setPrompts(data.prompts);
      setOriginalPrompts(data.prompts);
      setLoadSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prompts');
      setLoadSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const savePrompt = async () => {
    if (!isPromptMode(viewMode)) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // For now, we'll just save to R2 as system_prompt.md
      // In the future, could save individual phase prompts
      const response = await fetch(`${AI_SERVICE_URL()}/api/admin/prompts/${viewMode}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: prompts[viewMode] }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      setSuccess(`${getViewModeLabel(viewMode)} saved successfully!`);
      setOriginalPrompts({ ...originalPrompts, [viewMode]: prompts[viewMode] });

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isPromptMode(viewMode)) return;

    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      setPrompts({ ...prompts, [viewMode]: text });
      setSuccess(`File "${file.name}" loaded. Click "Save" to upload.`);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read file');
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleViewModeChange = (event: SelectChangeEvent<ViewMode>) => {
    setViewMode(event.target.value as ViewMode);
  };

  const isPromptMode = (mode: ViewMode): mode is keyof PromptsData => {
    return mode !== 'lastInput' && mode !== 'lastOutput';
  };

  const getViewModeLabel = (mode: ViewMode): string => {
    const labels: Record<ViewMode, string> = {
      intent: 'Phase 1: Intent (Parse Player Action)',
      narrativeExtension: 'Phase 4: Narrative Extension (THE COMEDY WRITER)',
      entityCreator: 'Phase 5: Entity Creator (Formatter/Translator)',
      actionExecution: 'Phase 6: Action Execution (Resolve Mechanics)',
      narrator: 'Phase 7: Narrator (THE PERFORMER)',
      lastInput: 'Last Input to AI',
      lastOutput: 'Last Output from AI',
    };
    return labels[mode];
  };

  const getCurrentContent = (): string => {
    if (viewMode === 'lastInput') return lastInput || 'No input captured yet';
    if (viewMode === 'lastOutput') return lastOutput || 'No output captured yet';
    return prompts[viewMode];
  };

  const hasChanges = isPromptMode(viewMode) && prompts[viewMode] !== originalPrompts[viewMode];

  // Load prompts and last input/output from localStorage
  useEffect(() => {
    loadPrompts();

    const storedInput = localStorage.getItem('ai_last_input');
    const storedOutput = localStorage.getItem('ai_last_output');

    if (storedInput) setLastInput(storedInput);
    if (storedOutput) setLastOutput(storedOutput);

    // Listen for updates from chat
    const handleStorageUpdate = () => {
      const input = localStorage.getItem('ai_last_input');
      const output = localStorage.getItem('ai_last_output');
      if (input) setLastInput(input);
      if (output) setLastOutput(output);
    };

    window.addEventListener('storage', handleStorageUpdate);
    // Also listen for custom event from same window
    window.addEventListener('ai-console-update', handleStorageUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageUpdate);
      window.removeEventListener('ai-console-update', handleStorageUpdate);
    };
  }, []);

  return (
    <Card
      className="panel"
      sx={{
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <CardContent className="panel-header" sx={{ p: 1.5, pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'info.main',
              fontWeight: 700,
              letterSpacing: 1,
              fontSize: '0.65rem',
            }}
          >
            AI Console - 8-Phase Orchestrator V2
          </Typography>
          {loadSuccess && (
            <>
              <Chip
                label="Stored in R2"
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
              />
              {hasChanges && (
                <Chip label="Unsaved" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }} />
              )}
            </>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
          Phase prompts and AI communication inspector
        </Typography>
      </CardContent>

      <Box className="panel-scroll" sx={{ px: 1.5, pb: 1.5 }}>
        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Loading state */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Content */}
        {!loading && (
          <>
            {/* View Mode Selector */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="view-mode-label">Select Prompt or View</InputLabel>
              <Select
                labelId="view-mode-label"
                id="view-mode-select"
                value={viewMode}
                label="Select Prompt or View"
                onChange={handleViewModeChange}
              >
                <MenuItem value="intent">{getViewModeLabel('intent')}</MenuItem>
                <MenuItem value="narrativeExtension">{getViewModeLabel('narrativeExtension')}</MenuItem>
                <MenuItem value="entityCreator">{getViewModeLabel('entityCreator')}</MenuItem>
                <MenuItem value="actionExecution">{getViewModeLabel('actionExecution')}</MenuItem>
                <MenuItem value="narrator">{getViewModeLabel('narrator')}</MenuItem>
                <MenuItem value="lastInput">{getViewModeLabel('lastInput')}</MenuItem>
                <MenuItem value="lastOutput">{getViewModeLabel('lastOutput')}</MenuItem>
              </Select>
            </FormControl>

            {/* Content Display */}
            <TextField
              fullWidth
              multiline
              rows={20}
              value={getCurrentContent()}
              onChange={(e) => {
                if (isPromptMode(viewMode)) {
                  setPrompts({ ...prompts, [viewMode]: e.target.value });
                }
              }}
              InputProps={{ readOnly: !isPromptMode(viewMode) }}
              placeholder={isPromptMode(viewMode) ? "Enter prompt..." : "No data captured yet"}
              variant="outlined"
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {getCurrentContent().length.toLocaleString()} characters • ~{Math.ceil(getCurrentContent().length / 4)} tokens
            </Typography>

            {/* Action Buttons */}
            {isPromptMode(viewMode) && (
              <Stack direction="row" spacing={2} sx={{ mt: 3 }} flexWrap="wrap">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".md,.txt"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />

                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={saving}
                >
                  Upload
                </Button>

                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={savePrompt}
                  disabled={!hasChanges || saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>

                <Button variant="outlined" onClick={loadPrompts} disabled={loading || saving}>
                  Reload
                </Button>
              </Stack>
            )}
          </>
        )}
      </Box>
    </Card>
  );
}
